import { Animation, createAnimation } from '@ionic/angular';

/** Cuánto se pasa el sheet de su alto final en el rebote. */
export const SHEET_BOUNCE_OFFSET = 10;
/** Clase del `ion-modal` que rellena el hueco bajo el sheet durante el rebote (global.scss). */
export const SHEET_BOUNCE_CLASS = 'sheet-bounce';

const SHEET_ENTER_MS = 320;
const SHEET_LEAVE_MS = 280;
const PAGE_TRANSITION_MS = 320;
const PAGE_SLIDE_OUT_MS = 140;
const PAGE_SLIDE_IN_MS = 220;
/** Margen tras el que se deja de esperar una animación que no termina. */
const SLIDE_WATCHDOG_MS = 150;

const EASE_IN = 'cubic-bezier(0.4, 0, 1, 1)';
const EASE_OUT = 'cubic-bezier(0, 0, 0.2, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.4, 0, 0.2, 1)';
/** Curva de las transiciones de página de Ionic en iOS. */
const PAGE_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

const ON_SCREEN = 'translateX(0)';
const OFF_LEFT = 'translateX(-100%)';
const OFF_RIGHT = 'translateX(100%)';
/** La página de atrás se corre solo un tercio, por detrás de la que entra, como en iOS. */
const BEHIND_LEFT = 'translateX(-33%)';

/** Lo que Ionic les pasa a `enterAnimation` y `leaveAnimation` de un sheet. */
export interface SheetAnimationOptions {
  readonly currentBreakpoint?: number;
}

/** Lo que Ionic le pasa a la animación de una transición de página. */
export interface PageTransitionOptions {
  readonly enteringEl: HTMLElement;
  readonly leavingEl?: HTMLElement;
  readonly direction?: string;
}

type Keyframes = Parameters<Animation['keyframes']>[0];

export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function sheetAnimation(
  modal: HTMLElement,
  name: string,
  durationMs: number,
  wrapperFrames: Keyframes,
  backdropFrom: string | number,
  backdropTo: string | number,
): Animation {
  const root = modal.shadowRoot as ShadowRoot;
  // Los ids son obligatorios: el gesto de arrastre del sheet de Ionic busca estas animaciones por id.
  const wrapper = createAnimation('wrapperAnimation')
    .addElement(root.querySelectorAll('.modal-wrapper, .modal-shadow'))
    .beforeStyles({ opacity: 1 })
    .easing('linear')
    .keyframes(wrapperFrames);
  const backdrop = createAnimation('backdropAnimation')
    .addElement(root.querySelector('ion-backdrop') as Element)
    .easing(EASE_OUT)
    .fromTo('opacity', backdropFrom, backdropTo);

  return createAnimation(name).addElement(modal).duration(durationMs).addAnimation([wrapper, backdrop]);
}

/**
 * Entrada del sheet: sube, se pasa apenas de su alto final y baja hasta quedar en su lugar.
 * Sin rama de movimiento reducido: Ionic reusa este builder para el gesto de arrastre y con
 * duración 0 el sheet quedaría abajo. Para eso, `animated: false` al crear el modal.
 */
export function sheetBounceEnter(modal: HTMLElement, _opts?: SheetAnimationOptions): Animation {
  return sheetAnimation(modal, 'sheet-bounce-enter', SHEET_ENTER_MS, [
    { offset: 0, opacity: 1, transform: 'translateY(100%)', easing: EASE_OUT },
    { offset: 0.65, opacity: 1, transform: `translateY(-${SHEET_BOUNCE_OFFSET}px)`, easing: EASE_IN_OUT },
    { offset: 1, opacity: 1, transform: 'translateY(0)' },
  ], 0, 'var(--backdrop-opacity)');
}

/** Salida del sheet: sube apenas y baja hasta salir. Si se cerró arrastrando ya está abajo: no rebota. */
export function sheetBounceLeave(modal: HTMLElement, opts?: SheetAnimationOptions): Animation {
  const alreadyDown = opts?.currentBreakpoint === 0;
  const frames: Keyframes = alreadyDown
    ? [
      { offset: 0, opacity: 1, transform: 'translateY(100%)' },
      { offset: 1, opacity: 1, transform: 'translateY(100%)' },
    ]
    : [
      { offset: 0, opacity: 1, transform: 'translateY(0)', easing: EASE_OUT },
      { offset: 0.3, opacity: 1, transform: `translateY(-${SHEET_BOUNCE_OFFSET}px)`, easing: EASE_IN },
      { offset: 1, opacity: 1, transform: 'translateY(100%)' },
    ];

  return sheetAnimation(modal, 'sheet-bounce-leave', SHEET_LEAVE_MS, frames, alreadyDown ? 0 : 'var(--backdrop-opacity)', 0);
}

/**
 * Entrar a un evento, volver a la lista o cambiar de rol navegando: slide horizontal de la página
 * entera. La transición de Ionic en iOS solo mueve un `ion-content` hijo directo de la página, así que
 * no alcanza al header ni a las pantallas con pestañas (reporte), y en Android es otra.
 */
export function rolePageTransition(_baseEl: unknown, opts: PageTransitionOptions): Animation {
  const back = opts.direction === 'back';
  const entering = createAnimation()
    .addElement(opts.enteringEl)
    .beforeRemoveClass('ion-page-invisible')
    .fill('both')
    .fromTo('transform', back ? BEHIND_LEFT : OFF_RIGHT, ON_SCREEN);
  const leaving = opts.leavingEl
    ? [createAnimation().addElement(opts.leavingEl).fill('both').fromTo('transform', ON_SCREEN, back ? OFF_RIGHT : BEHIND_LEFT)]
    : [];

  return createAnimation()
    .duration(prefersReducedMotion() ? 0 : PAGE_TRANSITION_MS)
    .easing(PAGE_EASING)
    .addAnimation([entering, ...leaving]);
}

/**
 * Si la WebView deja de pintar (app en segundo plano) la animación no termina: el cambio de rol
 * no puede quedar colgado de ella.
 */
function playWithin(animation: Animation, durationMs: number): Promise<void> {
  return Promise.race([
    animation.play(),
    new Promise<void>(resolve => setTimeout(resolve, durationMs + SLIDE_WATCHDOG_MS)),
  ]);
}

/**
 * Cambio de rol que se queda en la misma pantalla: la página sale por la izquierda, se aplica
 * `change` con ella fuera de la vista y vuelve a entrar por la derecha, como si fuera otra página.
 */
export async function slidePageAround(page: HTMLElement | null, change: () => Promise<void>): Promise<void> {
  if (!page || prefersReducedMotion()) {
    return change();
  }
  const slideOut = createAnimation().addElement(page).duration(PAGE_SLIDE_OUT_MS).easing(EASE_IN).fill('both')
    .fromTo('transform', ON_SCREEN, OFF_LEFT);
  await playWithin(slideOut, PAGE_SLIDE_OUT_MS);
  try {
    await change();
  } catch (error: unknown) {
    slideOut.destroy();
    throw error;
  }
  const slideIn = createAnimation().addElement(page).duration(PAGE_SLIDE_IN_MS).easing(EASE_OUT).fill('both')
    .fromTo('transform', OFF_RIGHT, ON_SCREEN);
  // Primero arranca la entrada y recién después se suelta la salida: si no, la página parpadea en su lugar.
  const slidIn = playWithin(slideIn, PAGE_SLIDE_IN_MS);
  slideOut.destroy();
  await slidIn;
  slideIn.destroy();
}
